var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { forwardRef } from 'react';
import IconButton from '@mui/material/IconButton';
import Typography from '../Typography';
import { styled } from '../styles';
import useTokens from '../utils/useTokens';
// Helper function to get the button size based on 'bdsSize'
const getSize = (bdsSize, theme) => {
    switch (bdsSize) {
        case 'medium':
            return { width: theme.palette.iconButton.size.medium, height: theme.palette.iconButton.size.medium };
        case 'input':
            return { width: theme.palette.iconButton.size.input, height: theme.palette.iconButton.size.input };
        case 'rectangle':
            return {
                width: theme.palette.iconButton.size.rectangle.width,
                height: theme.palette.iconButton.size.rectangle.height
            };
        default:
            return { width: theme.palette.iconButton.size.input, height: theme.palette.iconButton.size.input };
    }
};
// Helper function to get the icon size based on 'bdsSize'
const getIconSize = (bdsSize, theme) => {
    switch (bdsSize) {
        case 'medium':
            return theme.palette.icon.size.xsmall;
        case 'input':
            return theme.palette.icon.size.medium;
        case 'rectangle':
            return theme.palette.icon.size.small;
        default:
            return theme.palette.icon.size.medium;
    }
};
const StyledLabel = styled(Typography, {
    shouldForwardProp: (prop) => prop !== 'selected'
})(({ theme, selected }) => {
    const tokens = useTokens();
    return {
        fontFamily: theme.typography.fontFamily,
        fontSize: tokens.aliasesFontSizeMicro,
        fontWeight: theme.typography.fontWeightBold,
        // TODO: Update lineHeight to use tokens when Ann pushes update
        // lineHeight: tokens.aliasesLineHeightMicro,
        lineHeight: '12px',
        letterSpacing: tokens.aliasesLetterSpacingDefault,
        color: selected ? tokens.iconButtonColorTextSelected : tokens.iconButtonColorTextDefault,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        width: '100%'
    };
});
// For the styled component to work, we need this typed as CSSObject
// and the type of the styles prop to be Interpolation<Theme>
const StyledIconButton = styled(IconButton, {
    shouldForwardProp: (prop) => !['bdsSize', 'selected'].includes(prop)
})(({ bdsSize = 'input', selected, theme }) => {
    const iconSize = getIconSize(bdsSize, theme);
    const buttonSize = getSize(bdsSize, theme);
    return {
        // Button size (height/width)
        '&.MuiButtonBase-root': {
            height: buttonSize.height,
            width: buttonSize.width
        },
        // Icon size (height/width/fontSize)
        '& .MuiSvgIcon-root': {
            fill: 'currentColor',
            height: iconSize,
            width: iconSize,
            fontSize: iconSize
        },
        // TODO: Discuss with Ann (8px is default set by MUI)
        padding: bdsSize === 'rectangle' ? 0 : 8,
        color: selected
            ? theme.palette.icon.interactive.neutral.color.selected.default
            : theme.palette.icon.interactive.neutral.color.default,
        borderRadius: bdsSize === 'rectangle'
            ? theme.palette.iconButton.border.radius.rectangle
            : theme.palette.iconButton.border.radius.round,
        cursor: 'pointer',
        background: selected
            ? theme.palette.iconButton.interactive.subtle.selected.background
            : theme.palette.iconButton.interactive.subtle.background,
        border: `${theme.palette.iconButton.border.width} solid ${selected
            ? theme.palette.iconButton.interactive.subtle.selected.border
            : theme.palette.iconButton.interactive.subtle.border}`,
        '&:hover': {
            color: selected
                ? theme.palette.icon.interactive.neutral.color.selected.hover
                : theme.palette.icon.interactive.neutral.color.hover,
            background: selected
                ? theme.palette.iconButton.interactive.subtle.selected.hover.background
                : theme.palette.iconButton.interactive.subtle.hover.background,
            border: `${theme.palette.iconButton.border.width} solid ${selected
                ? theme.palette.iconButton.interactive.subtle.selected.hover.border
                : theme.palette.iconButton.interactive.subtle.hover.border}`
        },
        '&:focus-visible': {
            color: theme.palette.icon.interactive.neutral.color.focus,
            background: theme.palette.iconButton.interactive.subtle.background,
            border: `${theme.palette.iconButton.border.width} solid ${theme.palette.iconButton.interactive.subtle.border}`
        },
        '&.Mui-disabled': {
            color: theme.palette.icon.interactive.neutral.color.disabled,
            background: theme.palette.iconButton.interactive.subtle.disabled.background,
            border: `${theme.palette.iconButton.border.width} solid ${theme.palette.iconButton.interactive.subtle.disabled.border}`
        }
    };
});
// Forward ref to StyledIconButton
const BDSIconButton = forwardRef((_a, ref) => {
    var { selected = false, bdsSize, disabled, onClick, label, children } = _a, props = __rest(_a, ["selected", "bdsSize", "disabled", "onClick", "label", "children"]);
    const handleOnClick = (event) => {
        if (!disabled && onClick) {
            onClick(event);
        }
    };
    return (React.createElement(StyledIconButton, Object.assign({ ref: ref, selected: selected, bdsSize: bdsSize, onClick: handleOnClick, disabled: disabled }, props),
        React.createElement("div", { style: {
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: useTokens().iconButtonSpaceBetween,
                width: '100%'
            } },
            children,
            label && React.createElement(StyledLabel, { selected: selected }, label))));
});
BDSIconButton.displayName = 'BDSIconButton';
export default BDSIconButton;
//# sourceMappingURL=IconButton.js.map